import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import SectionHeading from './SectionHeading';
import TipsterLogo from './TipsterLogo';
import { useData } from '../context/DataContext';

export function PlanChip({ tipster, plan, selected, onSelect, asLink = true, dark = false }) {
  const Wrapper = asLink ? Link : 'button';
  const props = asLink ? { to: `/subscribe/${tipster.id}/${plan.days}` } : { type: 'button', onClick: () => onSelect(plan.days) };
  const hi = plan.popular;
  const light = (hi || dark) && !selected;
  return (
    <Wrapper
      {...props}
      data-testid={`plan-${tipster.id}-${plan.days}`}
      className={`relative text-left rounded-2xl p-4 border transition-all duration-300 w-full ${
        selected ? 'border-royal ring-2 ring-royal/30 bg-ice'
          : dark ? (hi ? 'border-signal/60 bg-white/10 text-white' : 'border-white/15 bg-white/[0.04] text-white hover:bg-white/10')
          : hi ? 'border-navy bg-navy text-white' : 'border-line bg-white hover:border-royal/50'
      }`}
    >
      {hi && !selected && <span className="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full bg-royal text-white text-[9px] font-mono font-bold uppercase tracking-wider" data-testid={`plan-popular-${tipster.id}`}>Most popular</span>}
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className={`text-[11px] font-mono uppercase tracking-wider ${light ? 'text-white/60' : 'text-slate-500'}`}>{plan.days} days</div>
          <div className={`text-xs mt-0.5 ${light ? 'text-white/70' : 'text-slate-500'}`}>≈ €{(plan.price / plan.days).toFixed(2)} / day</div>
        </div>
        <div className={`font-mono text-2xl font-bold ${light ? 'text-white' : 'text-navy'}`}>€{plan.price}</div>
      </div>
    </Wrapper>
  );
}

export default function Pricing() {
  const { tipsters } = useData();
  return (
    <section id="pricing" className="scroll-mt-24 py-24 md:py-32 bg-white" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          align="center"
          eyebrow="Pricing"
          title="Choose a tipster. Choose a duration. That is the whole decision."
          lead="Each subscription is independent — take one tipster or combine them. No tiers, no add-ons."
        />
        <div className="mt-16 grid md:grid-cols-3 gap-6 items-stretch">
          {tipsters.map((t, i) => (
            <div key={t.id} className={`relative rounded-[28px] border p-7 flex flex-col ${i === 0 ? 'bg-navy text-white border-navy grain overflow-hidden' : 'border-line bg-white'}`} data-testid={`pricing-${t.id}`}>
              <div className="flex items-center gap-4">
                <TipsterLogo tipster={t} className="w-12 h-12" />
                <div>
                  <h3 className={`font-display text-2xl font-bold ${i === 0 ? 'text-white' : 'text-navy'}`}>{t.name}</h3>
                  <div className="eyebrow mt-1" style={{ color: i === 0 ? '#19D3FF' : t.accent }}>{t.formatLabel}</div>
                </div>
              </div>
              <p className={`mt-4 text-sm ${i === 0 ? 'text-slate-300' : 'text-slate-600'}`}>{t.tagline}</p>
              <ul className="mt-5 space-y-2 flex-1">
                {[`One ${t.formatLabel} per day`, 'Delivered by email before kick-off', 'Full access to the results archive'].map(f => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <span className={`mt-0.5 w-4.5 h-4.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${i === 0 ? 'bg-royal text-white' : 'bg-ice text-royal'}`}><Check className="w-3 h-3" strokeWidth={3} /></span>
                    <span className={i === 0 ? 'text-slate-200' : 'text-slate-700'}>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-7 grid gap-3 pt-2">
                {t.plans.map(p => <PlanChip key={p.days} tipster={t} plan={p} dark={i === 0} />)}
              </div>
              <Link to={`/subscribe/${t.id}`} className={`mt-5 inline-flex items-center justify-center gap-2 h-12 rounded-full font-bold text-sm ${i === 0 ? 'bg-royal text-white hover:bg-royal-dark' : 'bg-navy text-white hover:bg-navy-soft'} transition-colors`} data-testid={`pricing-cta-${t.id}`}>
                Subscribe to {t.name} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
        <p className="mt-8 text-center text-xs text-slate-500 max-w-xl mx-auto">
          Prices in EUR. Payment by PayPal, Skrill, Neteller, bank card or crypto. 18+ only — betting involves risk and past results do not guarantee future outcomes.
        </p>
      </div>
    </section>
  );
}
